import AdminHeader from "components/Headers/AdminHeader";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Input,
  Tooltip,
  UncontrolledTooltip,
} from "reactstrap";
import { getCourseStatusColor } from "constants/CommonConstants";
import {
  renderStars,
  toCamelCase,
} from "components/CommonComponents/CommonFunctions";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import {
  COURSE_CONFIG,
  CONTENT_DELIVERY_MODE_MAP,
  COURSE_STATUS_OPTIONS,
} from "components/constant";
import { useDispatch, useSelector } from "react-redux";
import { getCourseList } from "action/course";
import { courseList } from "constants/course";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";

const Courses = () => {
  const dispatch = useDispatch();
  const isFirstSearchRender = useRef(true);
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const rolePath =
    role === "SUPER_ADMIN"
      ? "super-admin"
      : role === "ADMIN"
        ? "admin"
        : role === "CHANNEL_PARTNER"
          ? "channel-partner"
          : "instructor";
  const config = COURSE_CONFIG[role] || {};
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMyCourse = pathname.includes("/my-courses");
  const mode = isMyCourse ? "MY_COURSE" : "COURSE";
  const isMobile = window.innerWidth < 768;

  const [courseListState, setCourseListState] = useState({
    isLoading: true,
    isSearched: false,
    error: "",
    noRecords: false,
  });
  const [pageNum, setPageNum] = useState(1);
  const [showFilters, setShowFilters] = useState(false); // to show or hide the filters UI in mobile screen
  const [filterState, setFilterState] = useState({
    searchName: "",
    price: "All",
    level: ["All"],
    courseType: ["All"],
    contentDeliveryMode: ["All"],
    rating: "",
    status: [],
    filterParams: {
      filters: [],
      searchString: "",
    },
  });
  // const [isLiveChecked, setIsLiveChecked] = useState(true);
  const [tooltipOpen, setTooltipOpen] = useState({});
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const courseListData = useSelector((state) => state.course.courseList);
  const statistics = courseListData?.data?.CourseStatistic || {};
  const PRICE_FILTERS = [
    {
      label: "All",
      count: statistics?.PriceStatisticInfo?.all || 0,
    },
    {
      label: "Paid",
      count: statistics?.PriceStatisticInfo?.paid || 0,
    },
    {
      label: "Free",
      count: statistics?.PriceStatisticInfo?.free || 0,
    },
  ];

  const LEVEL_FILTERS = [
    {
      label: "All",
      count: statistics?.LevelStatisticInfo?.all || 0,
    },
    {
      label: "Beginner",
      count: statistics?.LevelStatisticInfo?.beginners || 0,
    },
    {
      label: "Intermediate",
      count: statistics?.LevelStatisticInfo?.intermediate || 0,
    },
    {
      label: "Advanced",
      count: statistics?.LevelStatisticInfo?.advanced || 0,
    },
  ];
  const REVIEW_FILTERS = [
    {
      stars: 5,
      count: statistics?.ReviewStatisticInfo?.five || 0,
    },
    {
      stars: 4,
      count: statistics?.ReviewStatisticInfo?.four || 0,
    },
    {
      stars: 3,
      count: statistics?.ReviewStatisticInfo?.three || 0,
    },
    {
      stars: 2,
      count: statistics?.ReviewStatisticInfo?.two || 0,
    },
    {
      stars: 1,
      count: statistics?.ReviewStatisticInfo?.one || 0,
    },
  ];

  const COURSE_TYPE_FILTERS = [
    {
      label: "All",
      count: statistics?.CourseTypeStatisticInfo?.all || 0,
    },
    {
      label: "Recorded",
      count: statistics?.CourseTypeStatisticInfo?.recorded || 0,
    },
    {
      label: "Live",
      count: statistics?.CourseTypeStatisticInfo?.live || 0,
      subTypes: [
        {
          label: "Offline",
          count: statistics?.ContentDeliveryModeStatisticInfo?.offline || 0,
        },
        {
          label: "Online",
          count: statistics?.ContentDeliveryModeStatisticInfo?.online || 0,
        },
        {
          label: "Hybrid",
          count: statistics?.ContentDeliveryModeStatisticInfo?.hybrid || 0,
        },
      ],
    },
  ];

  const STATUS_FILTERS = [
    {
      label: "Published",
      value: "PUBLISHED",
      count: statistics?.CourseStatusStatisticInfo?.published || 0,
    },
    {
      label: "Draft",
      value: "DRAFT",
      count: statistics?.CourseStatusStatisticInfo?.draft || 0,
    },
    // {
    //   label: "Ready to Publish",
    //   value: "READY_TO_PUBLISH",
    //   count: statistics?.CourseStatusStatisticInfo?.readyToPublish || 0,
    // }
  ];

  useEffect(() => {
    setCourseListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    getCourseList(
      dispatch,
      pageNum,
      filterState.filterParams,
      debouncedSearch,
      {
        sortBy: "CreatedAt",
        sortType: "desc",
      },
    );

    return () => {
      dispatch({ type: courseList, payload: {} });
    };
  }, [pageNum, filterState.filterParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterState.searchName);
    }, 500);

    return () => clearTimeout(timer);
  }, [filterState.searchName]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    // if (!filterState.searchName) {
    //   return;
    // }

    setPageNum(1);

    setFilterState((prev) => {
      const updatedState = {
        ...prev,
        searchName: debouncedSearch,
      };

      return {
        ...updatedState,
        filterParams: buildFilterParams(updatedState),
      };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    if (courseListData?.code === 200) {
      setCourseListState({
        isLoading: false,
        error: "",
        noRecords: courseListData?.data?.Course?.length === 0,
      });
    } else if (courseListData?.code || courseListData?.message) {
      setCourseListState({
        isLoading: false,
        error:
          courseListData?.message ||
          "Failed to load course list! Please try again.",
        noRecords: false,
      });
    }
  }, [courseListData]);

  const courses = courseListData?.data?.Course || [];

  const totalRecords = courseListData?.data?.totalRecords || 0;

  const totalFilteredRecords = courseListData?.data?.totalFilteredRecords || 0;

  const totalPages = Math.ceil(totalFilteredRecords / 9);

  const buildFilterParams = (state = filterState) => {
    const filters = [];

    if (state.price && state.price !== "All") {
      filters.push({
        field: "PriceType",
        condition: "eq",
        filterValues: [state.price.toUpperCase()],
      });
    }

    if (state.level.length) {
      const levels = state.level.filter((v) => v !== "All");

      if (levels.length) {
        filters.push({
          field: "Level",
          condition: "in",
          filterValues: levels.map((v) => v.toUpperCase()),
        });
      }
    }

    if (state.courseType.length) {
      const courseTypes = state.courseType.filter((v) => v !== "All");

      if (courseTypes.length) {
        filters.push({
          field: "CourseType",
          condition: "in",
          filterValues: courseTypes.map((v) => v.toUpperCase()),
        });
      }
    }

    if (state.rating) {
      filters.push({
        field: "Reviews",
        condition: "gte",
        filterValues: [String(state.rating)],
      });
    }

    if (state.status.length) {
      filters.push({
        field: "CourseStatus",
        condition: "in",
        filterValues: state.status.map((v) => v.toUpperCase()),
      });
    }

    if (state.contentDeliveryMode.length) {
      const modes = state.contentDeliveryMode.filter((v) => v !== "All");

      if (modes.length) {
        filters.push({
          field: "ContentDeliveryMode",
          condition: "in",
          filterValues: modes.map((v) => CONTENT_DELIVERY_MODE_MAP[v]),
        });
      }
    }

    return {
      filters: filters.length ? JSON.stringify(filters) : "",
    };
  };

  // Single select (radio) for price
  const handleSingleSelectFilter = (key, value) => {
    setCourseListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: false,
    }));
    setPageNum(1);

    setFilterState((prev) => {
      const updatedState = {
        ...prev,
        [key]: prev[key] === value ? "" : value,
      };

      return {
        ...updatedState,
        filterParams: buildFilterParams(updatedState),
      };
    });
  };

  // Multi select (checkbox)
  const handleMultiSelectFilter = (key, value) => {
    setCourseListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: false,
    }));
    setPageNum(1);

    setFilterState((prev) => {
      let selected = [...prev[key]];

      if (value === "All") {
        selected = selected.includes("All") ? [] : ["All"];
      } else {
        selected = selected.filter((v) => v !== "All");

        if (selected.includes(value)) {
          selected = selected.filter((v) => v !== value);
        } else {
          selected.push(value);
        }
      }

      const updatedState = {
        ...prev,
        [key]: selected,
      };

      // Reset content delivery mode when Live is not selected
      if (key === "courseType" && !selected.includes("Live")) {
        updatedState.contentDeliveryMode = ["All"];
      }

      return {
        ...updatedState,
        filterParams: buildFilterParams(updatedState),
      };
    });
  };

  const toggleTooltip = (key) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;

    setFilterState((prev) => ({
      ...prev,
      searchName: value,
    }));
  };

  const handleNavigation = (courseId) => {
    const targetPath = isMyCourse
      ? `/${rolePath}/my-courses/${urlEncodedId(courseId)}/details`
      : `/${rolePath}/course/${urlEncodedId(courseId)}/details`;

    navigate(targetPath, { state: { mode: mode } });
  };

  const hasActiveFilters =
    filterState.price !== "All" ||
    filterState.level.some((v) => v !== "All") ||
    filterState.courseType.some((v) => v !== "All") ||
    filterState.contentDeliveryMode.some((v) => v !== "All") ||
    filterState.status.length > 0 ||
    filterState.rating !== "" ||
    filterState.searchName !== "";

  const handleClearFilters = () => {
    setFilterState({
      searchName: "",
      price: "All",
      level: ["All"],
      courseType: ["All"],
      contentDeliveryMode: ["All"],
      rating: "",
      status: [],
      filterParams: {
        filters: [],
        searchString: "",
      },
    });
    setDebouncedSearch("");
    if (pageNum !== 1) setPageNum(1);
  };

  const isLiveChecked = filterState.courseType.includes("Live");

  const getCourseTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case "offline":
        return "Offline";
      case "online":
      case "live":
        return "Online";
      case "recorded":
        return "Recorded Course";
      case "both":
        return "Hybrid";
      default:
        return "Course";
    }
  };

  const getCourseTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "offline":
        return "ni ni-building";
      case "online":
      case "live":
        return "ni ni-world";
      case "recorded":
        return "ni ni-button-play";
      case "both":
        return "ni ni-app";
      default:
        return "ni ni-book-bookmark";
    }
  };

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6 mb-4">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mb-0 mt-4 text-white-input flex-shrink-0">Courses</h3>

          <div className="d-flex align-items-center ml-auto">
            <InputGroup className="mr-4 flex-grow-1 search-bottom-border">
              <Input
                type="text"
                placeholder="Search title, category"
                className="text-white-input"
                value={filterState.searchName}
                onChange={handleSearchChange}
              />

              <InputGroupAddon addonType="append">
                <InputGroupText className="search-icons">
                  {filterState.searchName && (
                    <i
                      className="fa fa-times clear-search"
                      onClick={() =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: "",
                        }))
                      }
                    />
                  )}

                  <i className="fa fa-search" />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>

            {COURSE_CONFIG[role]?.showAddCourse && (
              <Button
                className="text-nowrap ml-2"
                onClick={() =>
                  navigate(
                    role === "SUPER_ADMIN"
                      ? "/super-admin/course/add-course"
                      : "/admin/course/add-course",
                  )
                }
              >
                <i className="fa fa-plus mr-1" />
                Add Course
              </Button>
            )}
          </div>
        </div>

        <div className="d-lg-none d-flex justify-content-end mt-5 mb-1 w-100">
          <Button
            color="primary"
            className="btn-deep-blue"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        <Row className="mt-5">
          {/* left card gird for courses */}
          <Col xl="9" lg="8" md="12">
            {courseListState.isLoading ? (
              <div className="loaderContainer no-courses-container">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : courseListState.error ? (
              <div className="loaderContainer error-style no-courses-container">
                <div className="error-icon">
                  <i class="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{courseListState.error}</span>
              </div>
            ) : courseListState.noRecords ? (
              <div className="loaderContainer empty-style no-courses-container">
                <div className="empty-icon">
                  <i class="fa-solid fa-circle-info"></i>
                </div>
                <span>No courses found.</span>
              </div>
            ) : (
              // <div className="no-courses-container">
              //   <div className="no-courses-icon">
              //     <i className="fa fa-folder-open" />
              //   </div>

              //   <h3 className="no-courses-title">No courses found</h3>
              // </div>
              <>
                <div className="courses-grid">
                  {courses.map((course) => {
                    const [createdDate, createdTime] =
                      course.created_at?.split(", ") || [];
                    return (
                      <div key={course.id} className="course-card-wrapper">
                        <Card
                          className="course-card mb-1 cursor"
                          onClick={() => handleNavigation(course.id)}
                        >
                          {/* Course Image */}
                          <div className="course-image-wrapper">
                            {course.media_url?.match(
                              /\.(mp4|webm|ogg|mov|m4v)$/i,
                            ) ? (
                              <video
                                className="course-image"
                                src={course.media_url}
                                muted
                                preload="metadata"
                                playsInline
                                disablePictureInPicture
                                controls={false}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <img
                                src={course.media_url}
                                alt={course.course_name}
                                className="course-image"
                              />
                            )}

                            {/* Course Type Badge */}
                            {course.course_type && (
                              <span
                                className={`radio-badge image-radio-badge ${course.course_type === "LIVE"
                                  ? "live"
                                  : "recorded"
                                  }`}
                              >
                                <span className="radio-circle">
                                  <span className="radio-dot" />
                                </span>
                                {course.course_type}
                              </span>
                            )}

                            {/* Status Badge */}
                            {COURSE_CONFIG[role]?.showCourseStatus && (
                              <Badge
                                color={getCourseStatusColor(
                                  course.course_status,
                                )}
                                className="course-status-badge"
                              >
                                {COURSE_STATUS_OPTIONS.find(
                                  (item) => item.value === course.course_status,
                                )?.label || course.course_status}
                              </Badge>
                            )}

                            {course?.content_delivery_mode !== "" && (
                              <span className="course-overview__badges">
                                <span
                                  className={`course-type-pill ${course?.content_delivery_mode?.toLowerCase() ===
                                    "offline"
                                    ? "offline"
                                    : course?.content_delivery_mode?.toLowerCase() ===
                                      "recorded"
                                      ? "recorded"
                                      : course?.content_delivery_mode?.toLowerCase() ===
                                        "both"
                                        ? "hybrid"
                                        : "live"
                                    }`}
                                >
                                  <i
                                    className={getCourseTypeIcon(
                                      course?.content_delivery_mode,
                                    )}
                                  />
                                  {getCourseTypeLabel(
                                    course?.content_delivery_mode,
                                  )}
                                </span>
                              </span>
                            )}
                          </div>

                          {/* Course Details */}
                          <CardBody className="pt-3 pb-1 ml--1 mr--1">
                            {/* Course Title */}
                            {COURSE_CONFIG[role]?.showCourseName ? (
                              <div className="small-text text-default font-weight-bold">
                                {/* <div className="text-muted small">
                                  Course title
                                </div> */}

                                <div className="">
                                  <div
                                    id={`courseTitleTooltip-${course.id}`}
                                    className={
                                      isMobile
                                        ? "course-title-mobile-wrap"
                                        : "course-title-ellipsis"
                                    }
                                  >
                                    {course.course_name}
                                  </div>

                                  {!isMobile && (
                                    <Tooltip
                                      className="qb-tooltip"
                                      placement="auto"
                                      delay={{ show: 150, hide: 0 }}
                                      target={`courseTitleTooltip-${course.id}`}
                                      isOpen={tooltipOpen[`title-${course.id}`]}
                                      toggle={() =>
                                        toggleTooltip(`title-${course.id}`)
                                      }
                                      autohide={false}
                                    >
                                      {course.course_name}
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            ) : null}

                            {/* Desktop Course Details */}
                            <div className="course-details-desktop">
                              {/* Level + Category */}
                              <div className="d-flex justify-content-between mt-3">
                                <div>
                                  <div className="text-muted small">Level</div>
                                  <div className="xs-text">
                                    {toCamelCase(course.level)}
                                  </div>
                                </div>

                                <div className="text-right">
                                  <div className="text-muted small">
                                    Category
                                  </div>

                                  <div>
                                    <span
                                      id={`courseCategoryTooltip-${course.id}`}
                                      className="course-category-ellipsis xs-text"
                                    >
                                      {course.category}
                                    </span>
                                  </div>

                                  {/* Keep Category tooltip for web */}
                                  <Tooltip
                                    className="qb-tooltip"
                                    placement="auto"
                                    delay={{ show: 150, hide: 0 }}
                                    isOpen={
                                      tooltipOpen[`category-${course.id}`]
                                    }
                                    target={`courseCategoryTooltip-${course.id}`}
                                    toggle={() =>
                                      setTooltipOpen((prev) => ({
                                        ...prev,
                                        [`category-${course.id}`]:
                                          !prev[`category-${course.id}`],
                                      }))
                                    }
                                    autohide={false}
                                  >
                                    {course.category}
                                  </Tooltip>
                                </div>
                              </div>

                              {/* Rating/Enrollment + Enrollment/Created At */}
                              <div className="d-flex justify-content-between mt-3">
                                <div>
                                  <div className="text-muted small">
                                    {COURSE_CONFIG[role]?.showRating
                                      ? "Rating"
                                      : "Enrolled trainees"}
                                  </div>

                                  {COURSE_CONFIG[role]?.showRating ? (
                                    course.average_rating > 0 ? (
                                      <div className="rating-wrapper">
                                        {renderStars(course.average_rating)}
                                      </div>
                                    ) : (
                                      <div className="xs-text">
                                        No ratings yet
                                      </div>
                                    )
                                  ) : (
                                    <div className="xs-text">
                                      {course.enrolled_user_count}
                                    </div>
                                  )}
                                </div>

                                {COURSE_CONFIG[role]?.showRating ? (
                                  <div>
                                    <div className="text-muted small text-right">
                                      Enrollments
                                    </div>
                                    <div className="xs-text text-right">
                                      {course.enrolled_user_count}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-right">
                                    <div className="text-muted small">
                                      Created at
                                    </div>
                                    <div className="xs-text">
                                      <div>{createdDate || "-"}</div>
                                      <div>{createdTime || ""}</div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Created By + Created At */}
                              {COURSE_CONFIG[role]?.showCreatedbyCreatedAt ? (
                                <div className="d-flex justify-content-between mt-3">
                                  {COURSE_CONFIG[role]?.showCreatedBy ? (
                                    <div>
                                      <div className="text-muted small">
                                        Created by
                                      </div>

                                      <div
                                        id={`createdByTooltip-${course.id}`}
                                        className="xs-text created-by-ellipsis"
                                      >
                                        {course.created_by_name || "-"}
                                      </div>

                                      <Tooltip
                                        className="qb-tooltip"
                                        placement="auto"
                                        delay={{ show: 150, hide: 0 }}
                                        target={`createdByTooltip-${course.id}`}
                                        isOpen={
                                          tooltipOpen[`createdBy-${course.id}`]
                                        }
                                        toggle={() =>
                                          toggleTooltip(
                                            `createdBy-${course.id}`,
                                          )
                                        }
                                        autohide={false}
                                      >
                                        {course.created_by_name || "-"}
                                      </Tooltip>
                                    </div>
                                  ) : null}

                                  <div className="text-right">
                                    <div className="text-muted small">
                                      Created at
                                    </div>

                                    <div className="xs-text">
                                      <div>{createdDate || "-"}</div>
                                      <div>{createdTime || ""}</div>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>

                            {/* Mobile Course Details */}
                            <div className="course-details-mobile">
                              {/* Category - full width */}
                              <div className="course-mobile-full-row">
                                <div className="text-muted small">Category</div>
                                <div className="xs-text course-mobile-category">
                                  {course.category || "-"}
                                </div>
                              </div>

                              {/* Created By - full width */}
                              {COURSE_CONFIG[role]?.showCreatedBy ? (
                                <div className="course-mobile-full-row">
                                  <div className="text-muted small">
                                    Created by
                                  </div>

                                  <div className="xs-text">
                                    {course.created_by_name || "-"}
                                  </div>
                                </div>
                              ) : null}

                              {/* Level + Rating */}
                              <div>
                                <div className="text-muted small">Level</div>
                                <div className="xs-text course-mobile-ellipsis">
                                  {toCamelCase(course.level)}
                                </div>
                              </div>

                              <div>
                                <div className="text-muted small">Rating</div>

                                {course.average_rating > 0 ? (
                                  <div className="rating-wrapper">
                                    {renderStars(course.average_rating)}
                                  </div>
                                ) : (
                                  <div className="xs-text">No ratings yet</div>
                                )}
                              </div>

                              {/* Enrollment + Created At */}
                              <div>
                                <div className="text-muted small">
                                  Enrollment
                                </div>
                                <div className="xs-text">
                                  {course.enrolled_user_count}
                                </div>
                              </div>

                              <div>
                                <div className="text-muted small">
                                  Created at
                                </div>
                                <div className="xs-text">
                                  {course.created_at || "-"}
                                </div>
                              </div>
                            </div>

                            <div className="d-flex mt-3 mb-2 justify-content-between">
                              <div className="course-price">
                                <div className="text-muted small">Price</div>
                                <div className="small-text text-default font-weight-bold">
                                  {Number(course.price) > 0
                                    ? `₹${Number(course.price).toFixed(2)}`
                                    : "Free"}
                                </div>
                              </div>
                              <div className="d-flex align-items-center text-right mr--3 mt-2">
                                {config.showAddBatch &&
                                  course.course_status !== "DRAFT" && (
                                    <>
                                      <Button
                                        color="link"
                                        className="p-0 mr-2"
                                        id={`addbatch${course.id}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(
                                            role === "SUPER_ADMIN"
                                              ? "/super-admin/batch/add-batch"
                                              : "/admin/batch/add-batch", {
                                            state: { courseID: course.id },
                                          })
                                        }}
                                      >
                                        <i class="fa-solid fa-calendar-days"></i>
                                      </Button>
                                      <UncontrolledTooltip
                                        delay={0}
                                        target={`addbatch${course.id}`}
                                      >
                                        Add Batch
                                      </UncontrolledTooltip>
                                    </>
                                  )}

                                <Button
                                  color="link"
                                  className="p-0 mr-2"
                                  id={`view${course.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigation(course.id);
                                  }}
                                >
                                  <i className="fa fa-eye " />
                                </Button>
                                <UncontrolledTooltip
                                  delay={0}
                                  target={`view${course.id}`}
                                >
                                  View
                                </UncontrolledTooltip>

                                {/* {config.showEdit &&
                                  Number(course.enrolled_user_count) === 0 && ( */}
                                <>
                                  <Button
                                    color="link"
                                    className="p-0"
                                    id={`edit${course.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      navigate(
                                        role === "SUPER_ADMIN"
                                          ? `/super-admin/course/edit-course/${urlEncodedId(course.id)}`
                                          : `/admin/course/edit-course/${urlEncodedId(course.id)}`,
                                      );
                                    }}
                                  >
                                    <i className="fa fa-edit " />
                                  </Button>
                                  <UncontrolledTooltip
                                    delay={0}
                                    target={`edit${course.id}`}
                                  >
                                    Edit
                                  </UncontrolledTooltip>
                                </>
                                {/* )} */}
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    );
                  })}
                </div>
                <CustomPagination
                  totalPages={totalPages}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                />
              </>
            )}
          </Col>

          {/* Right: Filters */}
          <Col
            xl="3"
            lg="4"
            md="12"
            className={`filters-wrapper ${showFilters ? "show-filters" : ""}`}
          >
            <Card className="filters-card mb-4">
              <CardBody>
                <div className="filters-header">
                  <div>
                    <h5 className="text-muted mb-2">Filters</h5>
                    {hasActiveFilters ? (
                      <div className="filter-summary">
                        Showing <strong>{totalFilteredRecords}</strong> of{" "}
                        <strong>{totalRecords}</strong> Courses
                      </div>
                    ) : (
                      <div className="filter-summary">
                        <strong>{totalRecords}</strong> Courses
                      </div>
                    )}
                  </div>

                  {hasActiveFilters && (
                    <Button
                      color="link"
                      className="clear-btn p-0"
                      onClick={handleClearFilters}
                    >
                      <i className="fa fa-times mr-1 mt-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {/* Price */}
                <div className="mb-4">
                  <h5 className="text-muted mb-2">Price</h5>
                  {PRICE_FILTERS.map((item) => (
                    <div
                      key={item.label}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0">
                        <input
                          type="checkbox"
                          name="price"
                          className="mr-2"
                          checked={filterState.price === item.label}
                          onChange={() =>
                            handleSingleSelectFilter("price", item.label)
                          }
                        />
                        {item.label}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Levels */}
                <div className="mb-4">
                  <h5 className="text-muted mb-2">Levels</h5>
                  {LEVEL_FILTERS.map((item) => (
                    <div
                      key={item.label}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={filterState.level.includes(item.label)}
                          onChange={() =>
                            handleMultiSelectFilter("level", item.label)
                          }
                        />
                        {item.label}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Reviews */}
                {COURSE_CONFIG[role]?.showRating && (
                  <div className="mb-4">
                    <h5 className="text-muted mb-2">Reviews</h5>
                    {REVIEW_FILTERS.map((item) => (
                      <div
                        key={item.stars}
                        className="d-flex justify-content-between align-items-center mb-2"
                      >
                        <label className="mb-0 d-flex align-items-center">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={filterState.rating === String(item.stars)}
                            onChange={() =>
                              handleSingleSelectFilter(
                                "rating",
                                String(item.stars),
                              )
                            }
                          />
                          {renderStars(item.stars, false)}
                        </label>
                        <span className="text-muted small">{item.count}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Course Type */}
                <div>
                  <h5 className="text-muted mb-2">Course Type</h5>

                  {COURSE_TYPE_FILTERS.map((item) => (
                    <div key={item.label} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <label className="mb-0 cursor">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={filterState.courseType.includes(
                              item.label,
                            )}
                            onChange={(e) => {
                              handleMultiSelectFilter("courseType", item.label);

                              // if (item.label === "Live") {
                              //   setIsLiveChecked(e.target.checked);
                              // }
                            }}
                          />
                          {item.label}
                        </label>
                        <span className="text-muted small">{item.count}</span>
                      </div>

                      {/* Show sub-types only for Live */}
                      {item.label === "Live" &&
                        isLiveChecked &&
                        item.subTypes && (
                          <div className="mt-3">
                            <h5 className="text-muted mb-2">
                              Content Delivery Mode
                            </h5>
                            {item.subTypes.map((sub) => (
                              <div
                                key={sub.label}
                                className="d-flex justify-content-between align-items-center mb-1"
                              >
                                <label className="mb-0 cursor">
                                  <input
                                    type="checkbox"
                                    className="mr-2"
                                    checked={filterState.contentDeliveryMode.includes(
                                      sub.label,
                                    )}
                                    onChange={() =>
                                      handleMultiSelectFilter(
                                        "contentDeliveryMode",
                                        sub.label,
                                      )
                                    }
                                  />
                                  {sub.label}
                                </label>
                                <span className="text-muted small">
                                  {sub.count}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>

                {/* Status */}
                {COURSE_CONFIG[role]?.showCourseStatus && (
                  <div>
                    <h5 className="text-muted mb-2 mt-3">Status</h5>

                    {STATUS_FILTERS.map((item) => (
                      <div
                        key={item.label}
                        className="d-flex justify-content-between align-items-center mb-2"
                      >
                        <label className="mb-0">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={filterState.status.includes(item.value)}
                            onChange={() =>
                              handleMultiSelectFilter("status", item.value)
                            }
                          />
                          {item.label}
                        </label>

                        <span className="text-muted small">{item.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Courses;
